import {
    briefingFrequencyTypeMapToApi,
    BriefingRecurrenceFrequencyEnum
} from '../enums/briefing-recurrence-frequency.enum';
import { BriefingRecurrencePatternApiModel } from '../../../api/briefings/model/briefing-recurrence-pattern-api.model';
import { DeliveryRecurrencePatternModel } from '../../../Common/Forms/models/delivery-recurrence-pattern.model';
import * as _ from 'underscore';
import { BriefingRecurrenceFrequencyApiEnum } from '../../../api/briefings/enum/briefing-recurrence-frequency-api.enum';
import { TimeModel } from '../../../Common/Forms/models/time.model';
import { BriefingRecurrencePatternHoursApiModel } from '../../../api/briefings/model/briefing-recurrence-pattern-hours-api.model';
import { timeFormatsEnum } from '../../../Core/Users/enums/time-formats.enum';
import { MinuteModel } from '../../../Common/Forms/models/minute.model';
import { TwentyFourHourModel } from '../../../Common/Forms/models/twenty-four-hour.model';
import { TimeHelper } from '../../../Common/Forms/helpers/time.helper';
import { MonthDayPairApiModel } from '../../../api/briefings/model/month-day-pair-api.model';
import { MonthApiEnum } from '../../../api/briefings/enum/month-api.enum';

const timeHelper = new TimeHelper();

export class BriefingRecurrencePatternModel extends DeliveryRecurrencePatternModel {

    // Quarterly briefings publish the day after the end of each quarter (Jan 1, Apr 1, Jul 1, Oct 1).
    // Days are 0 based to match how the front end handles day-of-month selections.
    public static readonly QUARTERLY_MONTH_DAY_PAIRS: MonthDayPairApiModel[] = [
        { month: MonthApiEnum.JANUARY, days: [0] },
        { month: MonthApiEnum.APRIL, days: [0] },
        { month: MonthApiEnum.JULY, days: [0] },
        { month: MonthApiEnum.OCTOBER, days: [0] }
    ];

    /**
     * Class constructor
     */
    constructor(apiModel?: BriefingRecurrencePatternApiModel, timeZone?: string) {
        super();

        if (apiModel) {
            this.frequencyValues = apiModel.frequencyValues;
            this.timeZone = timeZone;
            this.times = this.convertApiHoursToTimeModel(apiModel.hours, timeZone);
            this.endDate = apiModel.endDate;

            if (apiModel.frequencyType === BriefingRecurrenceFrequencyApiEnum.quarterly) {
                this.frequencyType = BriefingRecurrenceFrequencyEnum.quarterly;
            } else if (apiModel.frequencyType === BriefingRecurrenceFrequencyApiEnum.daily) {
                // If every day is selected, we assume this is a "daily"
                if ([0, 1, 2, 3, 4, 5, 6].every(day => this.frequencyValues.indexOf(day) !== -1)) {
                    this.frequencyType = BriefingRecurrenceFrequencyEnum.daily;
                } else {
                    this.frequencyType = BriefingRecurrenceFrequencyEnum.weekly;
                }
            } else {
                this.frequencyType = BriefingRecurrenceFrequencyEnum.monthly;
            }
        } else {
            this.frequencyType = null;
            this.frequencyValues = [];
            this.times = [];
            this.endDate = null;
        }
    }

    /**
     * Compares this BriefingRecurrencePatternModel to another
     */
    public equals(other: BriefingRecurrencePatternModel): boolean {
        if (this.timeZone !== other.timeZone) {
            return false;
        }

        if (this.frequencyType !== other.frequencyType) {
            return false;
        }

        if (!this.isTimeInformationEqual(other.times)) {
            return false;
        }

        if (this.frequencyValues.sort().join() !== other.frequencyValues.sort().join()) {
            return false;
        }

        if (this.endDate !== other.endDate) {
            return false;
        }

        return true;
    }

    /**
     * Compare multiple times to see if we have logical equivalent set
     *
     * @param otherTimeSet
     */
    private isTimeInformationEqual(otherTimeSet: TimeModel[]): boolean {
        if (this.times.length !== otherTimeSet.length) {
            return false;
        }

        const timeHaystack = [...otherTimeSet];
        let found: boolean;
        for (const needle of this.times) {
            found = false;
            for (let i = 0; i < timeHaystack.length; i++) {
                if (needle.equals(timeHaystack[i])) {
                    found = true;
                    // We remove from future searches the element we just found; handy if somehow the exact same
                    // time is in the list more than once - we will ensure we have the same number of occurrences
                    // in both situations.
                    timeHaystack.splice(i, 1);
                    break;
                }
            }
            if (!found) {
                return false;
            }
        }

        return true;
    }

    /**
     * Maps the front end model to the API version (BriefingRecurrencePatternApiModel)
     */
    public toBriefingRecurrencePatternApiModel(): BriefingRecurrencePatternApiModel {
        const isQuarterly = this.frequencyType === BriefingRecurrenceFrequencyEnum.quarterly;
        const pattern = {
            frequencyType: briefingFrequencyTypeMapToApi.get(this.frequencyType as BriefingRecurrenceFrequencyEnum) as BriefingRecurrenceFrequencyApiEnum,
            frequencyValues: this.frequencyValues,
            hours: this.times.filter( timeModel => {
                // Get rid of blank elements
                return timeModel.hour !== null && timeModel.minute !== null;
            }).map(timeModel => {
                const timeModel24Hour = timeHelper.changeFormat(timeModel, timeFormatsEnum.twentyFourHour);
                return {
                    'hour': timeModel24Hour.hour,
                    'minute': timeModel24Hour.minute
                };
            }),
            endDate: this.endDate,
            // Quarterly publishes on a fixed set of months/days; other frequencies don't use this field
            monthDayPairs: isQuarterly ? BriefingRecurrencePatternModel.QUARTERLY_MONTH_DAY_PAIRS : []
        };

        return pattern;
    }

    private convertApiHoursToTimeModel(apiHours: BriefingRecurrencePatternHoursApiModel[], timeZone: string): TimeModel[] {
        const times: TimeModel[] = [];

        apiHours.forEach(
            apiHour => {
                const time = new TimeModel();
                time.format = timeFormatsEnum.twentyFourHour;
                // Trust the minute given from the backend is between 0-59
                time.minute = apiHour.minute as MinuteModel;
                // Trust the hour given from the backend is between 0-23
                time.hour = apiHour.hour as TwentyFourHourModel;
                time.timeZone = timeZone;

                times.push(time);
            }
        );

        return times;
    }

    /**
     * Generates appropriate localization tokens for describing the current reccurance pattern
     *
     * @returns string[] - localization tokens
     */
    public getRecurrenceDescription(): string[] {
        let reccuranceDescription = ['Briefing.recurring'];
        switch (this.frequencyType) {
            case BriefingRecurrenceFrequencyEnum.quarterly:
                reccuranceDescription = this.getQuarterlyDescription();
                break;
            case BriefingRecurrenceFrequencyEnum.monthly:
                reccuranceDescription = this.getMonthlyDescription();
                break;
            case BriefingRecurrenceFrequencyEnum.weekly:
                reccuranceDescription = this.getWeeklyDescription();
                break;
            case BriefingRecurrenceFrequencyEnum.daily:
                reccuranceDescription = this.getDailyDescription();
                break;
        }

        return reccuranceDescription;
    }

    /**
     * Gets localization tokens to describe the Monthly reccurance pattern
     *
     * @returns string[] - of localization tokens
     */
    private getMonthlyDescription(): string[] {
        // we want the months listed in order but dont want to change the model
        const daysScheduled = JSON.parse(JSON.stringify(this.frequencyValues)).sort((a, b) => a - b);
        const description = ['Briefing.monthly'];
        for (let i = 0; i < daysScheduled.length; i++) {
            description.push(daysScheduled[i] + 1);
        }

        return description;
    }

    /**
     * Gets localization tokens to describe the weekly reccurance pattern
     *
     * @returns string[] - localization tokens
     */
    private getWeeklyDescription(): string[] {
        const dayOfWeekLocalization: string[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        // we want the days listed in order but dont want to change the model
        const daysScheduled = JSON.parse(JSON.stringify(this.frequencyValues)).sort();
        const description: string[] = ['Briefing.weekly'];

        for (let i = 0; i < daysScheduled.length; i++) {
            const day: number = daysScheduled[i];
            description.push(dayOfWeekLocalization[day]);
        }

        return description;
    }

    /**
     * Gets localization token for describing daily recurrance pattern
     *
     * @returns string[] - localization tokens
    */
    private getDailyDescription(): string[] {
        return ['Briefing.daily'];
    }

    /**
     * Gets localization token for describing the quarterly recurrance pattern
     *
     * @returns string[] - localization tokens
     */
    private getQuarterlyDescription(): string[] {
        return ['Briefing.quarterly'];
    }

    public setTimeFormat(format: timeFormatsEnum) {
        this.times = this.times.map(time => time.changeFormat(format));
    }

}
